import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { ACCOUNT_MEMBERSHIPS_SEED, accountSystemRoleId } from '@common/data';
import { ACCOUNT_SETTINGS_RESOURCE_KEYS } from '@common/enums';
import type {
  AccountAccess,
  AccountAccessPage,
  AccountAccessQuery,
  AccountMembership,
  AccountSettingsAccess,
  Property,
  Role,
  RolePermissions,
} from '@common/types';

import { OrganizationService } from './organization.service';
import { RolesService } from './roles.service';

const LIST_LATENCY_MS = 150;

/**
 * The signed-in user's account memberships — which accounts they belong to and
 * what their role there lets them do. Membership is per account, so an
 * organisation's account list and the user's account list are not the same
 * thing; every caller that needs "accounts I can work with" asks here.
 */
@Injectable({ providedIn: 'root' })
export class AccountAccessService {
  private readonly organizations = inject(OrganizationService);
  private readonly roles = inject(RolesService);

  private readonly memberships = signal<ReadonlyArray<AccountMembership>>(
    ACCOUNT_MEMBERSHIPS_SEED,
  );

  /** Accounts the user belongs to inside the organisation they're working in. */
  readonly activeOrganizationAccess = computed<ReadonlyArray<AccountAccess>>(() =>
    this.accessForOrganization(this.organizations.activeId()),
  );

  readonly activeOrganizationAccessCount = computed<number>(
    () => this.activeOrganizationAccess().length,
  );

  accessForOrganization(orgId: string | null): ReadonlyArray<AccountAccess> {
    if (!orgId) return [];
    return this.organizations
      .accountsFor(orgId)
      .map((account) => this.toAccess(account))
      .filter((access): access is AccountAccess => access !== null);
  }

  isMember(accountId: string): boolean {
    return this.roleFor(accountId) !== null;
  }

  roleFor(accountId: string): Role | null {
    const membership = this.memberships().find((entry) => entry.accountId === accountId);
    if (!membership) return null;
    return this.roles.getById(accountSystemRoleId(membership.roleTemplateId, accountId)) ?? null;
  }

  settingsAccess(accountId: string): AccountSettingsAccess {
    const role = this.roleFor(accountId);
    return role ? this.resolveSettingsAccess(role.permissions) : 'none';
  }

  canViewSettings(accountId: string): boolean {
    return this.settingsAccess(accountId) !== 'none';
  }

  canEditSettings(accountId: string): boolean {
    return this.settingsAccess(accountId) === 'edit';
  }

  list(orgId: string | null, query: AccountAccessQuery): Observable<AccountAccessPage> {
    let result = this.accessForOrganization(orgId).slice();

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter(
        (access) =>
          access.name.toLowerCase().includes(term) ||
          access.location.toLowerCase().includes(term) ||
          access.region.toLowerCase().includes(term) ||
          access.roleName.toLowerCase().includes(term),
      );
    }

    const regionFilters = query.filters['region'];
    if (regionFilters?.length) {
      result = result.filter((access) => regionFilters.includes(access.region));
    }

    const accessFilters = query.filters['settingsAccess'];
    if (accessFilters?.length) {
      result = result.filter((access) => accessFilters.includes(access.settingsAccess));
    }

    if (query.sortField) {
      const field = query.sortField;
      const direction = query.sortOrder === 'desc' ? -1 : 1;
      result.sort((a, b) => {
        const av = this.sortValue(a, field);
        const bv = this.sortValue(b, field);
        if (av < bv) return -1 * direction;
        if (av > bv) return 1 * direction;
        return 0;
      });
    }

    const total = result.length;
    const start = Math.max(0, query.first);
    const data = result.slice(start, start + Math.max(1, query.rows));

    return of({ data, total }).pipe(delay(LIST_LATENCY_MS));
  }

  private toAccess(account: Property): AccountAccess | null {
    const role = this.roleFor(account.id);
    if (!role) return null;
    return {
      ...account,
      roleId: role.id,
      roleName: role.name,
      settingsAccess: this.resolveSettingsAccess(role.permissions),
    };
  }

  private resolveSettingsAccess(permissions: RolePermissions): AccountSettingsAccess {
    let access: AccountSettingsAccess = 'none';
    for (const key of ACCOUNT_SETTINGS_RESOURCE_KEYS) {
      const actions = permissions[key];
      if (!actions?.length) continue;
      if (actions.includes('edit') || actions.includes('manage')) return 'edit';
      if (actions.includes('view')) access = 'view';
    }
    return access;
  }

  private sortValue(access: AccountAccess, field: string): string {
    switch (field) {
      case 'location':
        return access.location.toLowerCase();
      case 'region':
        return access.region.toLowerCase();
      case 'roleName':
        return access.roleName.toLowerCase();
      case 'settingsAccess':
        return access.settingsAccess;
      default:
        return access.name.toLowerCase();
    }
  }
}
